import { useCallback, useMemo, useState } from 'react';

import { discountsApi, profileApi, ProfileModel, InventoryModel } from '@pukka/api';
import { LeavePrompt, NoDataDisplay, PageContainer, ContentContainer, DrawerContainer, Table, Text } from '@pukka/ui-components';
import { DefaultActionBar, OutletTooltip } from '@pukka/ui-composite';
import { useSnapshot } from '@pukka/valtio';
import { CellContext, ColumnDef, Row } from '@tanstack/react-table';

import DiscountCreateBox from './DiscountCreateBox';
import DiscountEditBox from './DiscountEditBox';
import discountState from './discountStore';

const allOutlet: ProfileModel.Outlet = {
  id: undefined,
  name: 'All Outlets',
};

function AmountCell({ row }: CellContext<InventoryModel.Discount.List200ResponseInner, unknown>) {
  return <span>{row.original.amount_format}</span>;
}

function OutletCell({ row }: CellContext<InventoryModel.Discount.List200ResponseInner, unknown>) {
  const { data: profile } = profileApi.useProfile();
  // all we need is only "Name" but it's not available from discount api response,
  // and only available from profile api response.
  // so we need to do this 2 loops just to find the name of each outlets.
  // can we have outletname from discount response?
  const outlets = row.original.outlets.map((outletDiscount) => {
    const id = outletDiscount.outlet_id;
    const outlet = profile?.outlets?.find((o) => o.id === id) || {};

    return outlet;
  });

  if (!row.original.total_outlets || row.original.total_outlets === 1) {
    return <span>{outlets[0]?.name || '-'}</span>;
  }

  return (
    <OutletTooltip outlets={outlets} totalOutlets={row.original.total_outlets}>
      <div className="inline rounded bg-light-blue-500 px-3 py-1.5 text-sm text-white">{row.original.total_outlets} Outlets</div>
    </OutletTooltip>
  );
}

function Discount() {
  const { data: profile } = profileApi.useProfile();
  const [selectedOutlet, setSelectedOutlet] = useState<ProfileModel.Outlet>(allOutlet);
  const [actionType, setActionType] = useState<'create' | 'edit' | null>(null);
  const [selectedDiscount, setSelectedDiscount] = useState<Row<InventoryModel.Discount.List200ResponseInner> | null>(null);
  const { dirtyFormExists } = useSnapshot(discountState);
  const [leaveCb, setLeaveCb] = useState<(payload: unknown) => void>();
  const { data, fetchNextPage, isFetchingNextPage, isLoading } = discountsApi.useDiscounts({
    perPage: 25,
    outletId: selectedOutlet?.id,
  });
  const flatData = useMemo(() => data?.pages?.flatMap((page) => page.results) ?? [], [data]);
  const columns = useMemo<ColumnDef<InventoryModel.Discount.List200ResponseInner>[]>(
    () => [
      {
        header: 'Discount Name',
        accessorKey: 'name',
        minSize: 120,
      },
      {
        header: 'Amount',
        accessorKey: 'amount',
        cell: AmountCell,
        minSize: 120,
      },
      {
        header: 'Outlet',
        accessorKey: 'outlets',
        minSize: 120,
        cell: OutletCell,
      },
    ],
    [],
  );

  const onBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;

        if (scrollHeight - scrollTop - clientHeight < 100) {
          if (!isFetchingNextPage) {
            fetchNextPage();
          }
        }
      }
    },
    [isFetchingNextPage, fetchNextPage],
  );

  const onBoxClose = () => {
    setActionType(null);
    setSelectedDiscount(null);
    discountState.dirtyFormExists = false;
  };

  const rowOnClick = (row: Row<InventoryModel.Discount.List200ResponseInner>) => {
    const openEdit = () => {
      setActionType('edit');
      setSelectedDiscount(row);
      discountState.dirtyFormExists = false;
    };

    if (dirtyFormExists) {
      setLeaveCb(() => () => openEdit());
    } else {
      openEdit();
    }
  };

  const onCreateClick = () => {
    const openCreate = () => {
      setActionType('create');
      setSelectedDiscount(null);
      discountState.dirtyFormExists = false;
    };

    if (dirtyFormExists) {
      setLeaveCb(() => () => openCreate());
    } else {
      openCreate();
    }
  };

  const onOutletChange = (outlet: ProfileModel.Outlet) => {
    const changeOutlet = () => {
      setSelectedOutlet(outlet);
      setActionType(null);
      setSelectedDiscount(null);
      discountState.dirtyFormExists = false;
    };

    if (dirtyFormExists) {
      setLeaveCb(() => () => changeOutlet());
    } else {
      changeOutlet();
    }
  };

  return (
    <PageContainer>
      <DefaultActionBar
        pageTitle="Discounts"
        buttonName="Create Discount"
        buttonOnClick={onCreateClick}
        selectedOutlet={{
          value: selectedOutlet?.name ?? '',
          key: selectedOutlet?.id,
          meta: selectedOutlet,
        }}
        outlets={[allOutlet].concat(profile?.outlets || [])}
        onOutletChange={onOutletChange}
      />
      <div className="mt-7 flex h-full">
        <ContentContainer className={actionType ? 'w-2/4' : 'w-full'}>
          {flatData.length === 0 || isLoading ? (
            <NoDataDisplay
              isLoading={isLoading}
              placeholder={
                <div className="flex space-x-2">
                  <Text variant="body-1" color="neutral">
                    Learn more about
                  </Text>
                  <Text
                    as="a"
                    href="https://help.mokapos.com/s/article/900000441563?language=in"
                    variant="body-1"
                    color="link"
                    className="italic hover:underline"
                    target="_blank"
                  >
                    Create Discount
                  </Text>
                </div>
              }
            />
          ) : (
            <Table
              data={flatData}
              columns={columns}
              bordered
              onBottomReached={onBottomReached}
              rowOnClick={rowOnClick}
              highlightedRowIndexes={[selectedDiscount?.index ?? -1]}
            />
          )}
        </ContentContainer>
        <DrawerContainer open={!!actionType} title={actionType === 'create' ? 'Create Discount' : 'Edit Discount'} onClose={onBoxClose}>
          {actionType === 'create' && <DiscountCreateBox onClose={onBoxClose} />}
          {actionType === 'edit' && selectedDiscount && <DiscountEditBox discount={selectedDiscount.original} onClose={onBoxClose} />}
        </DrawerContainer>
      </div>
      <LeavePrompt open={!!leaveCb} onClose={() => setLeaveCb(undefined)} onLeave={leaveCb} />
    </PageContainer>
  );
}

export default Discount;
